import type { InvoiceSpecSnapshot } from '@/lib/invoiceSpecs';
import { supabase } from '@/lib/supabase';

export interface PublicInvoiceShopInfo {
  shop_name: string;
  slogan?: string | null;
  address?: string | null;
  city?: string | null;
  phone?: string | null;
  phone_secondary?: string | null;
  phone_tertiary?: string | null;
  whatsapp?: string | null;
  email?: string | null;
  invoice_footer?: string | null;
  default_warranty_months?: number | null;
}

export interface PublicInvoiceData {
  invoice_number: string;
  sale_date: string;
  customer_name: string;
  customer_phone?: string | null;
  customer_email?: string | null;
  subtotal: number;
  discount: number;
  tax: number;
  total: number;
  amount_paid: number;
  balance_due: number;
  payment_status: string;
  shop?: PublicInvoiceShopInfo | null;
  items: Array<InvoiceSpecSnapshot & {
    item_name: string;
    item_type: string;
    sku?: string | null;
    serial_number?: string | null;
    specs?: string | null;
    quantity: number;
    unit_price: number;
    line_discount: number;
    line_total: number;
  }>;
  payments: Array<{
    payment_date: string;
    amount: number;
    payment_method: string;
    reference?: string | null;
  }>;
  error?: string;
}

export interface ShareTokenResult {
  success: boolean;
  token_id: string;
  token: string;
  expires_at: string | null;
}

export function normalizePakistanPhone(phone: string): string {
  let cleaned = phone.replace(/[^\d+]/g, '');
  if (cleaned.startsWith('+')) {
    cleaned = cleaned.substring(1);
  } else if (cleaned.startsWith('00')) {
    cleaned = cleaned.substring(2);
  } else if (cleaned.startsWith('03')) {
    cleaned = '92' + cleaned.substring(1);
  }
  return cleaned;
}

export async function createInvoiceShareToken(
  saleId: string,
  expiresInDays: number = 30
): Promise<ShareTokenResult> {
  const { data, error } = await supabase.rpc('create_invoice_share_token', {
    p_sale_id: saleId,
    p_expires_in_days: expiresInDays,
  });

  if (error) throw error;
  return data as ShareTokenResult;
}

export async function revokeInvoiceShareToken(token: string): Promise<{ success: boolean }> {
  const { data, error } = await supabase.rpc('revoke_invoice_share_token', {
    p_token: token,
  });

  if (error) throw error;
  return data as { success: boolean };
}

export async function fetchPublicInvoiceByToken(token: string): Promise<PublicInvoiceData> {
  const { data, error } = await supabase.rpc('get_public_invoice_by_token', {
    p_token: token,
  });

  if (error) throw error;
  return data as PublicInvoiceData;
}

/** Browser clients may only INSERT with status='prepared'. Service role sets sent/failed. */
export async function logInvoiceDelivery(params: {
  saleId: string;
  tokenId?: string | null;
  channel: 'whatsapp' | 'email' | 'link_copy';
  recipient?: string | null;
  metadata?: Record<string, any>;
}): Promise<void> {
  const { error } = await supabase.from('invoice_delivery_logs').insert({
    sale_id: params.saleId,
    token_id: params.tokenId || null,
    channel: params.channel,
    recipient: params.recipient || null,
    status: 'prepared', // always prepared from browser; service_role updates to sent/failed
    metadata: params.metadata || {},
  });

  if (error) {
    console.warn('Could not record delivery log:', error.message);
  }
}

export async function fetchInvoiceShareTokens(saleId: string) {
  const { data, error } = await supabase
    .from('invoice_share_tokens')
    .select('*')
    .eq('sale_id', saleId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
}

export async function fetchInvoiceDeliveryLogs(saleId: string) {
  const { data, error } = await supabase
    .from('invoice_delivery_logs')
    .select('*')
    .eq('sale_id', saleId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
}

